/**
 * NCR Details form body — all input rows for the Create NCR screen.
 * Extracted from NCRCreate.tsx to keep files under the 500-line limit (CR-6387).
 * Purely presentational: all state + handlers arrive via props.
 */
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Textarea } from '../../ui/textarea';
import { Button } from '../../ui/button';
import { Loader2, Save, Search, CheckCircle2 } from 'lucide-react';
import NCRContainmentSection from '../NCRContainmentSection';
import YesNoSelect from './YesNoSelect';
import type { SourceOfRejection, FaultCodeGroup, FaultCodeCategory, UserProfile } from '../../../lib/api';
import { useMenuSettings } from '../../../contexts/MenuSettingsContext';
import { useFormatters } from '../../../contexts/PreferencesContext';
import { displayPrice } from '../../../lib/unitPrice';

export interface NCRFormData {
  supplier: number | null;
  ncr_number: string;
  part_number: string;
  description: string;
  batch_number: string;
  // Held as strings so the box can genuinely be empty. As numbers they could
  // only ever be 0, which reads as "zero rejects" rather than "not filled in".
  batch_qty: string;
  reject_qty: string;
  shortage_qty: string;
  defect_description: string;
  grn_number: string;
  po_number: string;
  unit_price: string;
  works_order: string;
  po_line: number | null;
  po_line_number: string;
  grn: number | null;
  source_of_rejection_option: number | null;
  fault_group: number | null;
  fault_category: number | null;
  liability_type: number;
  is_collection_required: boolean | null;
  is_credit_required: boolean | null;
  is_replacement_required: boolean | null;
  is_internal: boolean;
  assigned_to: number | null;
  buyer_name: string;
  is_8d_required: boolean | null;
  containment_required: boolean;
  containment_qty_ok: string;
  containment_qty_not_ok: string;
  containment_current_stock: string;
}

export interface RepeatInfo {
  is_repeat: boolean;
  previous_count: number;
  last_ncr_number: string | null;
}

interface NCRDetailsFormProps {
  formData: NCRFormData;
  fieldErrors: Record<string, string>;
  updateField: (field: keyof NCRFormData, value: string | number | boolean | null) => void;
  onContainmentChange: (field: 'containment_required' | 'containment_qty_ok' | 'containment_qty_not_ok' | 'containment_current_stock', value: boolean | string) => void;
  suppliers: Array<{ id: number; name: string }>;
  loadingSuppliers: boolean;
  grnLinked: boolean;
  lookingUpGRN: boolean;
  doGRNLookup: (grnNumber: string, suppliersList: Array<{ id: number; name: string }>) => Promise<boolean>;
  sourcesOfRejection: SourceOfRejection[];
  faultGroups: FaultCodeGroup[];
  filteredCategories: FaultCodeCategory[];
  customerUsers: UserProfile[];
  repeatInfo: RepeatInfo | null;
  currentStock: number | null;
  saving: boolean;
  onSubmit: () => void;
  onCancel: () => void;
}

/**
 * Strip everything that isn't a digit.
 *
 * The quantity fields map to `PositiveIntegerField`, so a decimal point, a
 * minus sign or the `e` that `type="number"` happily accepts are all invalid.
 * Filtering on the way in keeps the box empty-able while making a bad value
 * impossible to type, rather than catching it at save time.
 *
 * @param value - raw input value
 * @returns the digits only, possibly an empty string
 */
function digitsOnly(value: string): string {
  return value.replace(/\D/g, '');
}

export default function NCRDetailsForm({
  formData, fieldErrors, updateField, onContainmentChange,
  suppliers, loadingSuppliers, grnLinked, lookingUpGRN, doGRNLookup,
  sourcesOfRejection, faultGroups, filteredCategories, customerUsers,
  repeatInfo, currentStock, saving, onSubmit, onCancel,
}: NCRDetailsFormProps) {
  // When the admin has made fault classification optional, drop the * markers.
  const { ncrFaultClassificationOptional } = useMenuSettings();
  const { preferences } = useFormatters();
  const priceDecimals = preferences.unit_price_decimals;
  const faultRequiredMark = ncrFaultClassificationOptional ? '' : ' *';
  return (
    <Card>
      <CardHeader>
        <CardTitle>NCR Details</CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Row 1: GRN and Site NCR */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="grn_number">GRN No.</Label>
            <div className="flex gap-2">
              <Input
                id="grn_number"
                value={formData.grn_number}
                onChange={(e) => updateField('grn_number', e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    if (formData.grn_number.trim()) {
                      doGRNLookup(formData.grn_number.trim(), suppliers);
                    }
                  }
                }}
                placeholder="Enter GRN number to auto-fill..."
                className={grnLinked ? 'border-green-500' : ''}
              />
              <Button
                variant="outline"
                size="icon"
                onClick={() => {
                  if (formData.grn_number.trim()) {
                    doGRNLookup(formData.grn_number.trim(), suppliers);
                  }
                }}
                disabled={lookingUpGRN || !formData.grn_number.trim()}
                title="Look up GRN to auto-populate fields"
                aria-label="Look up GRN to auto-populate fields"
              >
                {lookingUpGRN ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : grnLinked ? (
                  <CheckCircle2 className="h-4 w-4 text-green-500" />
                ) : (
                  <Search className="h-4 w-4" />
                )}
              </Button>
            </div>
            {grnLinked && <p className="text-xs text-green-600 mt-1">GRN linked - fields auto-populated</p>}
          </div>
          <div>
            <Label htmlFor="ncr_number">Company NCR/SRN No.</Label>
            <Input
              id="ncr_number"
              value={formData.ncr_number}
              onChange={(e) => updateField('ncr_number', e.target.value)}
              placeholder="Optional"
              className={fieldErrors['ncr_number'] ? 'border-red-500' : undefined}
              aria-invalid={!!fieldErrors['ncr_number'] || undefined}
              aria-describedby={fieldErrors['ncr_number'] ? 'ncr_number-error' : undefined}
            />
            {fieldErrors['ncr_number'] && <p id="ncr_number-error" role="alert" className="text-xs text-red-600 mt-1">{fieldErrors['ncr_number']}</p>}
          </div>
        </div>

        {/* Row 2: Supplier */}
        <div>
          <Label htmlFor="supplier">Supplier *</Label>
          <select
            id="supplier"
            value={formData.supplier || ''}
            onChange={(e) => updateField('supplier', e.target.value ? parseInt(e.target.value) : null)}
            className={`w-full h-10 px-3 rounded-md border bg-background text-sm ${fieldErrors['supplier'] ? 'border-red-500' : 'border-input'}`}
            disabled={loadingSuppliers}
            aria-invalid={!!fieldErrors['supplier'] || undefined}
            aria-describedby={fieldErrors['supplier'] ? 'supplier-error' : undefined}
          >
            <option value="">Select a supplier...</option>
            {suppliers.map(supplier => (
              <option key={supplier.id} value={supplier.id}>{supplier.name}</option>
            ))}
          </select>
          {fieldErrors['supplier'] && <p id="supplier-error" role="alert" className="text-xs text-red-600 mt-1">{fieldErrors['supplier']}</p>}
        </div>

        {/* Row 3: PO No. and PO Line No. */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="po_number">PO No.</Label>
            <Input id="po_number" value={formData.po_number} onChange={(e) => updateField('po_number', e.target.value)} />
          </div>
          <div>
            <Label htmlFor="po_line_number">PO Line No.</Label>
            <Input
              id="po_line_number"
              value={formData.po_line_number}
              onChange={(e) => updateField('po_line_number', e.target.value)}
              placeholder={grnLinked ? '' : 'Auto-filled from GRN'}
              readOnly={grnLinked}
              className={grnLinked ? 'bg-muted' : ''}
            />
          </div>
        </div>

        {/* Row 4: Part Details */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="part_number">Part No.</Label>
            <Input
              id="part_number"
              value={formData.part_number}
              onChange={(e) => updateField('part_number', e.target.value)}
              className={fieldErrors['part_number'] ? 'border-red-500' : undefined}
              aria-invalid={!!fieldErrors['part_number'] || undefined}
              aria-describedby={fieldErrors['part_number'] ? 'part_number-error' : undefined}
            />
            {fieldErrors['part_number'] && <p id="part_number-error" role="alert" className="text-xs text-red-600 mt-1">{fieldErrors['part_number']}</p>}
          </div>
          <div>
            <Label htmlFor="batch_number">Date Code/Batch No.</Label>
            <Input id="batch_number" value={formData.batch_number} onChange={(e) => updateField('batch_number', e.target.value)} />
          </div>
        </div>

        <div>
          <Label htmlFor="description">Part Description</Label>
          <Textarea id="description" value={formData.description} onChange={(e) => updateField('description', e.target.value)} rows={2} />
        </div>

        {/* Row 5: Unit price and works order */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="unit_price">Unit Price</Label>
            {/* `step="any"` deliberately: Decimal Precision sets the default a
                prefilled price arrives at, it does not cap what can be typed.
                A fixed step would make the browser reject 10.375 as invalid on
                a company configured for 2. */}
            <Input
              id="unit_price"
              type="number"
              step="any"
              min="0"
              value={formData.unit_price}
              onChange={(e) => updateField('unit_price', e.target.value)}
              placeholder={displayPrice(0, priceDecimals)}
            />
          </div>
          <div>
            <Label htmlFor="works_order">Works Order No.</Label>
            <Input id="works_order" value={formData.works_order} onChange={(e) => updateField('works_order', e.target.value)} placeholder="Optional" />
          </div>
        </div>

        {/* Row 6: Classification (mandatory) */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <Label htmlFor="source_of_rejection_option">Source of Rejection *</Label>
            <select
              id="source_of_rejection_option"
              value={formData.source_of_rejection_option || ''}
              onChange={(e) => updateField('source_of_rejection_option', e.target.value ? parseInt(e.target.value) : null)}
              className={`w-full h-10 px-3 rounded-md border bg-background text-sm ${fieldErrors['source_of_rejection_option'] ? 'border-red-500' : 'border-input'}`}
              aria-invalid={!!fieldErrors['source_of_rejection_option'] || undefined}
              aria-describedby={fieldErrors['source_of_rejection_option'] ? 'source_of_rejection_option-error' : undefined}
            >
              <option value="">Select...</option>
              {sourcesOfRejection.map(s => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
            </select>
            {fieldErrors['source_of_rejection_option'] && <p id="source_of_rejection_option-error" role="alert" className="text-xs text-red-600 mt-1">{fieldErrors['source_of_rejection_option']}</p>}
          </div>
          <div>
            <Label htmlFor="fault_group">Fault Group{faultRequiredMark}</Label>
            <select
              id="fault_group"
              value={formData.fault_group || ''}
              onChange={(e) => updateField('fault_group', e.target.value ? parseInt(e.target.value) : null)}
              className={`w-full h-10 px-3 rounded-md border bg-background text-sm ${fieldErrors['fault_group'] ? 'border-red-500' : 'border-input'}`}
              aria-invalid={!!fieldErrors['fault_group'] || undefined}
              aria-describedby={fieldErrors['fault_group'] ? 'fault_group-error' : undefined}
            >
              <option value="">Select...</option>
              {faultGroups.map(g => (
                <option key={g.id} value={g.id}>{g.name}</option>
              ))}
            </select>
            {fieldErrors['fault_group'] && <p id="fault_group-error" role="alert" className="text-xs text-red-600 mt-1">{fieldErrors['fault_group']}</p>}
          </div>
          <div>
            <Label htmlFor="fault_category">Fault Category{faultRequiredMark}</Label>
            <select
              id="fault_category"
              value={formData.fault_category || ''}
              onChange={(e) => updateField('fault_category', e.target.value ? parseInt(e.target.value) : null)}
              className={`w-full h-10 px-3 rounded-md border bg-background text-sm ${fieldErrors['fault_category'] ? 'border-red-500' : 'border-input'}`}
              disabled={!formData.fault_group}
              aria-invalid={!!fieldErrors['fault_category'] || undefined}
              aria-describedby={fieldErrors['fault_category'] ? 'fault_category-error' : undefined}
            >
              <option value="">{formData.fault_group ? 'Select...' : 'Select fault group first'}</option>
              {filteredCategories.map(c => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
            {fieldErrors['fault_category'] && <p id="fault_category-error" role="alert" className="text-xs text-red-600 mt-1">{fieldErrors['fault_category']}</p>}
          </div>
        </div>

        {/* Repeat Issue — auto-flagged when this part + fault group was
            already raised on a previous claim (customer review 2026-07). */}
        {formData.part_number.trim() && formData.fault_group && repeatInfo && (
          <div className="flex items-center gap-2 text-sm">
            <span className="text-muted-foreground">Repeat Issue:</span>
            {repeatInfo.is_repeat ? (
              <span
                className="px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 text-red-800"
                title={`${repeatInfo.previous_count} previous claim${repeatInfo.previous_count === 1 ? '' : 's'} for this part + fault group${repeatInfo.last_ncr_number ? ` (last: ${repeatInfo.last_ncr_number})` : ''}`}
              >
                Yes — {repeatInfo.previous_count} previous claim{repeatInfo.previous_count === 1 ? '' : 's'}
              </span>
            ) : (
              <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-800">No</span>
            )}
          </div>
        )}

        {/* Row 7: Buyer */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="buyer_name">Buyer</Label>
            <select
              id="buyer_name"
              value={formData.buyer_name}
              onChange={(e) => updateField('buyer_name', e.target.value)}
              className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
            >
              <option value="">Select Buyer...</option>
              {customerUsers.map((u) => (
                <option key={u.id} value={`${u.user.first_name} ${u.user.last_name}`.trim()}>
                  {`${u.user.first_name} ${u.user.last_name}`.trim() || u.user.email}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Row 8: Liability & Requirements */}
        <div className="border-t pt-4 mt-2">
          <h4 id="liability-heading" className="text-sm font-semibold mb-1">Liability <span className="text-red-500">*</span></h4>
          <p className="text-xs text-muted-foreground mb-3">Please Select</p>
          <div className="grid grid-cols-2 gap-3 max-w-lg" role="group" aria-labelledby="liability-heading" aria-invalid={!!fieldErrors['liability_type'] || undefined} aria-describedby={fieldErrors['liability_type'] ? 'liability_type-error' : undefined}>
            <button
              type="button"
              onClick={() => updateField('liability_type', 2)}
              className={`p-3 rounded-lg border-2 text-sm font-medium transition-all cursor-pointer ${
                formData.liability_type === 2
                  ? 'border-emerald-500 text-emerald-600 bg-emerald-50 dark:bg-emerald-900/20'
                  : `${fieldErrors['liability_type'] ? 'border-red-500' : 'border-gray-200'} text-gray-600 hover:border-gray-300 dark:border-gray-700 dark:text-gray-400`
              }`}
            >
              Customer Liability
            </button>
            <button
              type="button"
              onClick={() => updateField('liability_type', 1)}
              className={`p-3 rounded-lg border-2 text-sm font-medium transition-all cursor-pointer ${
                formData.liability_type === 1
                  ? 'border-emerald-500 text-emerald-600 bg-emerald-50 dark:bg-emerald-900/20'
                  : `${fieldErrors['liability_type'] ? 'border-red-500' : 'border-gray-200'} text-gray-600 hover:border-gray-300 dark:border-gray-700 dark:text-gray-400`
              }`}
            >
              Supplier Liability
            </button>
          </div>
          {fieldErrors['liability_type'] && <p id="liability_type-error" role="alert" className="text-xs text-red-600 mt-1">{fieldErrors['liability_type']}</p>}

          {/* Internal handling — Customer Liability only. Resolved in-house,
              never sent to the supplier; assigned to an internal user. */}
          {formData.liability_type === 2 && (
            <div className="mt-4 rounded-lg border border-dashed border-amber-300 bg-amber-50/40 p-3 dark:border-amber-700 dark:bg-amber-900/10">
              <label className="flex items-center gap-2 text-sm font-medium cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.is_internal}
                  onChange={(e) => updateField('is_internal', e.target.checked)}
                />
                Handle internally (do not send to supplier)
              </label>
              {formData.is_internal && (
                <div className="mt-3">
                  <Label className="text-xs">Assigned To (internal team)</Label>
                  <select
                    value={String(formData.assigned_to ?? '')}
                    onChange={(e) => updateField('assigned_to', e.target.value ? Number(e.target.value) : null)}
                    className={`w-full h-9 px-3 rounded-md border bg-background text-sm ${fieldErrors['assigned_to'] ? 'border-red-500' : 'border-input'}`}
                    aria-invalid={!!fieldErrors['assigned_to'] || undefined}
                    aria-describedby={fieldErrors['assigned_to'] ? 'assigned_to-error' : undefined}
                  >
                    <option value="">Unassigned</option>
                    {customerUsers.map((u) => (
                      <option key={u.id} value={u.user.id}>
                        {`${u.user.first_name} ${u.user.last_name}`.trim() || u.user.email}
                      </option>
                    ))}
                  </select>
                  {fieldErrors['assigned_to'] && <p id="assigned_to-error" role="alert" className="text-xs text-red-600 mt-1">{fieldErrors['assigned_to']}</p>}
                </div>
              )}
            </div>
          )}

          {formData.liability_type > 0 && (
            <div className="mt-4">
              <p className="text-xs font-semibold text-muted-foreground mb-3">
                Required actions for the {formData.liability_type === 1 ? 'supplier' : 'customer'}:
              </p>
              <div className={`grid ${formData.liability_type === 1 ? 'grid-cols-2 sm:grid-cols-4 max-w-3xl' : 'grid-cols-2 max-w-lg'} gap-3`}>
                <YesNoSelect id="is_8d_required" label="8D Required" value={formData.is_8d_required} onChange={(v) => updateField('is_8d_required', v)} error={fieldErrors['is_8d_required']} />
                <YesNoSelect id="is_collection_required" label="Collection Required" value={formData.is_collection_required} onChange={(v) => updateField('is_collection_required', v)} error={fieldErrors['is_collection_required']} />
                {formData.liability_type === 1 && (
                  <YesNoSelect id="is_credit_required" label="Credit Required" value={formData.is_credit_required} onChange={(v) => updateField('is_credit_required', v)} error={fieldErrors['is_credit_required']} />
                )}
                {formData.liability_type === 1 && (
                  <YesNoSelect id="is_replacement_required" label="Replacement Required" value={formData.is_replacement_required} onChange={(v) => updateField('is_replacement_required', v)} error={fieldErrors['is_replacement_required']} />
                )}
              </div>
            </div>
          )}
        </div>

        {/* Row 9: Quantities */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <Label htmlFor="batch_qty">Batch Qty</Label>
            <Input id="batch_qty" inputMode="numeric" placeholder="0" value={formData.batch_qty} onChange={(e) => updateField('batch_qty', digitsOnly(e.target.value))} />
          </div>
          <div>
            <Label htmlFor="reject_qty">Reject Qty</Label>
            <Input id="reject_qty" inputMode="numeric" placeholder="0" value={formData.reject_qty} onChange={(e) => updateField('reject_qty', digitsOnly(e.target.value))} />
          </div>
          <div>
            <Label htmlFor="shortage_qty">Shortage Qty</Label>
            <Input id="shortage_qty" inputMode="numeric" placeholder="0" value={formData.shortage_qty} onChange={(e) => updateField('shortage_qty', digitsOnly(e.target.value))} />
          </div>
        </div>

        {/* Stock Containment (customer review 2026-07) */}
        <NCRContainmentSection
          containmentRequired={formData.containment_required}
          qtyOk={formData.containment_qty_ok}
          qtyNotOk={formData.containment_qty_not_ok}
          currentStock={currentStock}
          manualStock={formData.containment_current_stock}
          onChange={onContainmentChange}
          errorOk={fieldErrors['containment_qty_ok']}
          errorNotOk={fieldErrors['containment_qty_not_ok']}
        />

        {/* Defect Description */}
        <div>
          <Label htmlFor="defect_description">Defect Description *</Label>
          <Textarea
            id="defect_description"
            value={formData.defect_description}
            onChange={(e) => updateField('defect_description', e.target.value)}
            rows={4}
            placeholder="Describe the defect or non-conformance..."
            className={fieldErrors['defect_description'] ? 'border-red-500' : undefined}
            aria-invalid={!!fieldErrors['defect_description'] || undefined}
            aria-describedby={fieldErrors['defect_description'] ? 'defect_description-error' : undefined}
          />
          {fieldErrors['defect_description'] && <p id="defect_description-error" role="alert" className="text-xs text-red-600 mt-1">{fieldErrors['defect_description']}</p>}
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-3 pt-4 border-t">
          <Button variant="outline" onClick={onCancel}>Cancel</Button>
          <Button onClick={onSubmit} disabled={saving}>
            {saving ? (
              <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Creating...</>
            ) : (
              <><Save className="h-4 w-4 mr-2" />Create NCR</>
            )}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
